import React from "react";
import { useReportContext } from "./ReportContext";
import {
  IReportElement,
  IReportElementTransform,
  IReportSlide,
} from "./ReprortContextTypes";
import { useSlideContext } from "./SlideContext";
import { toPng, toJpeg, toBlob, toPixelData, toSvg } from "html-to-image";
import RenderLimiter from "./RenderLimiter";
import { DataElement } from "./types";
import { HTMLtoParagraphs } from "./paragraphs";

const DomElement = (
  props: React.PropsWithChildren<{
    selector: string;
    typename: "image" | "text" | "html";
    pollInterval?: number;
    slideElement: string;
    transform?: IReportElementTransform;
  }>
) => {
  // TODO: after rendering the element fetch the text data from inside of it
  const ref = React.useRef<HTMLDivElement>(null);
  const slide = useSlideContext();
  const [completed, setCompleted] = React.useState(false);
  const element = React.useRef<IReportElement>({
    isReady: false,
    type: "text",
    text: "",
    slideElement: props.slideElement,
    transform: props.transform,
    elementDefinition: slide!.slideDefinition?.elements
      .filter((el) => el.tag === props.slideElement)
      .pop(),
  });
  const report = useReportContext();
  React.useEffect(() => {
    slide?.addElement?.(element.current);
  }, [ref, report]);

  // Wait for the element to appear in the DOM
  React.useEffect(() => {
    const selector = props.selector;
    const iv = window.setInterval(() => {
      const foundElement = ref.current?.querySelector(selector);
      // NOTE the old implemenation used: res(elem as HTMLDivElement);
      if (foundElement) {
        const elem = foundElement as HTMLDivElement;
        window.clearInterval(iv);
        if (element.current) {
          if (props.typename === "html") {
            const htmlText = elem.innerHTML;
            element.current.text = "";
            element.current.isReady = true;
            element.current.type = "text";
            const renderedElement = JSON.parse(
              JSON.stringify(element.current.elementDefinition)
            ) as DataElement;
            if (renderedElement && renderedElement.paragraphs) {
              const para = renderedElement.paragraphs[0];
              const port = para?.portions?.[0];
              const paragraphs = HTMLtoParagraphs(htmlText, {
                fillColor: {
                  r: 40,
                  g: 40,
                  b: 40,
                },
                fontSize: port?.fontSize || para.fontSize || 16,
                fontName: port?.fontName || para.fontName || "Verdana",
                textAlign: (port?.textAlign || para.textAlign || "left") as any,
              });

              renderedElement.paragraphs = paragraphs;
              renderedElement.slideElement = renderedElement.tag;
              element.current.rendered = renderedElement;
            }

            slide.onElementReady?.(element.current);
            setCompleted(true);
          }
          if (props.typename === "text") {
            element.current.text = elem.innerText || "";
            element.current.isReady = true;
            element.current.type = "text";

            const renderedElement = JSON.parse(
              JSON.stringify(element.current.elementDefinition)
            ) as DataElement;
            if (renderedElement && renderedElement.paragraphs) {
              const para = renderedElement.paragraphs[0];
              const port = para?.portions?.[0];
              if (port) {
                renderedElement.paragraphs = [para];
                para.portions = [port];
                para.text = "";
                port.text = elem.innerText || "";
                renderedElement.slideElement = renderedElement.tag;
                element.current.rendered = renderedElement;
              }
            }

            slide.onElementReady?.(element.current);
            setCompleted(true);
          }
          if (props.typename === "image") {
            toPng(elem, { pixelRatio: 1.25 })
              .then((value) => {
                const base = value.replace(
                  /^data:image\/(png|jpg);base64,/,
                  ""
                );
                element.current.type = "image";
                element.current.imagevalue = base;
                slide.onElementReady?.(element.current);
                setCompleted(true);
              })
              .catch((e) => {
                element.current.type = "clear";
                element.current.isReady = true;
                element.current.isEmpty = true;
                slide.onElementReady?.(element.current);
                setCompleted(true);
              });
          }
        }
      }
      const noDataSelector = ref.current?.querySelector(".reportNoData");
      if (noDataSelector) {
        window.clearInterval(iv);
        if (element.current) {
          element.current.isReady = true;
          element.current.isEmpty = true;
          slide.onElementReady?.(element.current);
        }
      }
    }, props.pollInterval || 100);
    return () => {
      window.clearInterval(iv);
    };
  }, [ref, ref.current]);

  return (
    <div ref={ref}>
      <RenderLimiter hasCompleted={completed}>{props.children}</RenderLimiter>
    </div>
  );
};

export default DomElement;
